<template>
  <div>
    <ValidationObserver @submit="formCheck">
      <UiModalContainer
        footer-classes="flex justify-end"
        :modal-show="modal"
        get-student-attendances
        :modal-width="50"
        @handleClick="handleClick"
      >
        <template v-slot:header>
          <span v-i18n="attendanceTranslation">MARK_DAILY_STUDENT_ATTENDANCE</span>
        </template>
        <template v-slot>
          <div class="main-content">
            <div>
              <div class="bg-primary-white border-primary-purple-100 pb-1">
                <UiDatePicker v-model="attendanceDate" />
              </div>

              <Loader v-if="isLoading" :content="true" class="mt-20 mb-48" />
              <div v-else>
                <div v-if="!isLoading && isEmpty(currentList)" class="my-10">
                  <NoRecordFound />
                </div>
                <div v-else>
                  <div class="font-medium text-xl text-label-text my-5">Students</div>
                  <TableWrapper id="section-attendance-table">
                    <THead>
                      <TRHead>
                        <TH>
                          <span v-i18n="attendanceTranslation">Student Name</span>
                        </TH>
                        <TH>
                          <span class="flex items-center gap-2.5">
                            <UiCheckbox
                              :model-value="AllChecked"
                              :no-error="true"
                              @change="setAllChecked(!AllChecked)"
                            />
                            <span v-i18n="attendanceTranslation">PRESENT</span>
                          </span>
                        </TH>
                        <TH>
                          <span class="flex items-center gap-2.5">
                            <UiCheckbox
                              :model-value="AllTardyChecked"
                              :no-error="true"
                              @change="setAllTardyChecked(!AllTardyChecked)"
                            />
                            <span v-i18n="attendanceTranslation">TARDY</span>
                          </span>
                        </TH>
                        <TH>
                          <span v-i18n="attendanceTranslation">COMMENT</span>
                        </TH>
                      </TRHead>
                    </THead>
                    <TBody v-if="currentList.length">
                      <TRBody
                        v-for="(student, index) in currentList"
                        :key="student.id + forceUpdate"
                      >
                        <TD>
                          <SingleUserDisplay :user="student" label="full_name" :image="true" />
                        </TD>
                        <!-- STATUS CHECKBOX -->
                        <TD>
                          <UiCheckbox
                            type="checkbox"
                            :no-error="true"
                            :name="`status-${index}`"
                            :model-value="student.status"
                            @change="updateStatus(index, $event)"
                          />
                        </TD>

                        <!-- TARDY CHECKBOX -->
                        <TD>
                          <UiCheckbox
                            type="checkbox"
                            :no-error="true"
                            :name="`tardy-${index}`"
                            :model-value="student.isTardy"
                            @change="updateTardy(index, $event)"
                          />
                        </TD>

                        <TD>
                          <InputBox
                            v-model="student.comment"
                            type="text"
                            title="Comment"
                            :name="`Comment ${index} ${student.id}`"
                            :hide-title="true"
                            placeholder="Comment"
                            :no-error="true"
                          />
                        </TD>
                      </TRBody>
                    </TBody>
                  </TableWrapper>
                </div>
              </div>
              <Pagination
                v-if="dayViewCount > RECORD_LIMIT"
                :record-limit="RECORD_LIMIT"
                :max-range="paginationCounts(dayViewCount, RECORD_LIMIT)"
                @filterRecord="handleFilterRecord"
              />
            </div>
          </div>
        </template>
        <template v-slot:footer>
          <div class="w-full -mt-4">
            <div
              v-if="dayViewCount > RECORD_LIMIT"
              v-i18n="Footer"
              class="xl:text-lg sm:text-base text-sm text-primary-purple-600"
            >
              ATTENDANCE_NOTE
            </div>
            <div class="flex gap-2.5 mt-4 justify-end">
              <UIButton @click="handleClick('close')">Cancel</UIButton>
              <UIButton
                button="primary"
                :disabled="disableButton"
                :class="disableButton ? 'disabled-btn' : ''"
              >
                <span v-if="isApiLoading"><Loader /></span>
                <span v-else>Submit</span>
              </UIButton>
            </div>
          </div>
        </template>
      </UiModalContainer>
    </ValidationObserver>
  </div>
</template>

<script>
import {
  paginationRangeHandler,
  getPageNumberWithRange,
} from '@src/components/BaseComponent/pagination.util.js'
import UiCheckbox from '@components/UiElements/UiCheckbox.vue'
import { mapActions, mapGetters } from 'vuex'
import Loader from '@components/BaseComponent/Loader.vue'
import SingleUserDisplay from '@src/components/UiElements/SingleUserDisplay.vue'
import FILTER_KEYS from '@src/constants/filter-constants.js'

import { utcOffsetZero } from '@src/utils/format-date.js'
import Pagination from '@components/BaseComponent/Pagination.vue'
import generalMixin from '@src/mixins/general-mixins.js'
import GENERAL, { ATTENDANCE } from '@src/constants/general-constants'
import UiModalContainer from '@components/UiElements/UiModalContainer.vue'
import isEmpty from 'lodash/isEmpty'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import { fullName } from '@/src/utils/settings/tenant-user.util'
import { objectDeepCopy } from '@src/utils/generalUtil.js'
import { Form as ValidationObserver } from 'vee-validate'
import dateTimeFormat from '@src/constants/date-time-constants.js'
import { formatDateFromLocalToUTC } from '@/src/utils/moment.util.js'
import InputBox from '@components/UiElements/UiInputBox.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import UiDatePicker from '@/src/components/UiElements/UiDatePicker.vue'
export default {
  components: {
    UiDatePicker,
    UiCheckbox,
    ValidationObserver,
    Loader,
    SingleUserDisplay,
    Pagination,
    UiModalContainer,
    NoRecordFound,
    InputBox,
    UIButton,
    TableWrapper,
    TBody,
    TD,
    TH,
    THead,
    TRBody,
    TRHead,
  },
  mixins: [generalMixin],
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
  },
  page: {
    title: 'Attendance | Section Attendance',
    meta: [
      {
        name: 'description',
        content: 'Section Attendance  of each subject class ',
      },
    ],
  },
  emits: ['toggle', 'fetch'],
  data() {
    return {
      GENERAL,
      Footer: 'Footer',
      options: ['Select'],
      isLoading: false,
      isApiLoading: false,
      studentList: [],
      attendanceDate: formatDateFromLocalToUTC(new Date(), dateTimeFormat.DATE),
      dailyAttendanceList: [],
      attendanceCache: {},
      AllChecked: false,
      AllTardyChecked: false,
      RECORD_LIMIT: GENERAL.RECORD_LIMIT,
      dayViewCount: 0,
      attendanceTranslation: 'attendanceTranslation',
      forceUpdate: 1,
      disableButton: true,
    }
  },
  computed: {
    ...mapGetters('layout', ['campusTimeZone']),
    /**
     * CURRENT ACTIVE LIST
     */
    currentList() {
      return this.dailyAttendanceList?.length > 0 ? this.dailyAttendanceList : this.studentList
    },
  },
  watch: {
    attendanceDate: {
      deep: true,
      async handler(value) {
        if (value) {
          this.attendanceCache = {}
          this.dailyAttendanceList = []

          await this.loadDayRecord()
          if (this.dailyAttendanceList?.length === 0) {
            await this.loadStudentList()
          }
        }
      },
    },

    /**
     * UPDATING ALL CHECKED WHENEVER ARRAY LIST CHANGES
     */
    currentList: {
      deep: true,
      handler() {
        this.allCheckForFilterRecord()
      },
    },
  },
  async mounted() {
    await this.fetchAttendance()
  },
  methods: {
    async fetchAttendance(range = { skip: GENERAL.RECORD_SKIP, limit: GENERAL.RECORD_LIMIT }) {
      await this.loadDayRecord(range)
      if (this.dailyAttendanceList?.length === 0) {
        await this.loadStudentList(range)
      }
    },
    mapAttendanceStatus(list) {
      return list.map((student) => {
        return {
          user_id: student.user_id ?? student.id,
          status: student.isTardy
            ? ATTENDANCE.TARDY
            : student.status
            ? ATTENDANCE.PRESENT
            : ATTENDANCE.ABSENT,
          comment: student.comment ? student.comment : '',
          attendance_date: this.attendanceDate,
        }
      })
    },
    updateAllRecordsList() {
      const allPages = Object.values(this.attendanceCache).flat()
      this.dailyAttendanceList = allPages
      this.allCheckForFilterRecord()
    },
    async handleFilterRecord(range) {
      if (this.dailyAttendanceList?.length > 0) {
        await this.loadDayRecord(range)
      } else {
        await this.loadStudentList(range)
      }
      this.checkIfAllPagesVisited()
    },

    async loadDayRecord(range = {}) {
      const paginationPayload = paginationRangeHandler(range)
      const pageNo = getPageNumberWithRange(paginationPayload, this.RECORD_LIMIT)

      const isPageAlreadyExists = this.attendanceCache?.hasOwnProperty(`${pageNo}`)
      const date = this.attendanceDate

      const payload = {
        ...paginationPayload,
        $where: {
          ...range?.$where,
          ...this.filtersData?.$where,
          attendance_date: {
            [FILTER_KEYS.GREATER_THAN_EQUAL]: date,
            [FILTER_KEYS.LESS_THAN_EQUAL]: date,
          },
        },
      }

      if (isPageAlreadyExists) {
        this.dailyAttendanceList = this.attendanceCache[`${pageNo}`] || []
        this.isLoading = false
        return this.dailyAttendanceList
      }

      try {
        this.isLoading = true
        const resp = await this.getDailyAttendanceList(payload)
        const records = objectDeepCopy(resp?.records || [])
        if (records.length === 0) {
          this.dayViewCount = resp?.meta?.total_records || 0
          return []
        }
        this.dayViewCount = resp?.meta?.total_records
        records.forEach((student) => {
          const selectedStatus = student.status
          student.status = selectedStatus === ATTENDANCE.PRESENT
          student.isTardy = selectedStatus === ATTENDANCE.TARDY
          student.first_name = student.user?.first_name
          student.last_name = student.user?.last_name
          student.full_name = `${student.first_name} ${student.last_name}`
        })

        this.attendanceCache = {
          ...this.attendanceCache,
          [pageNo]: records,
        }

        this.dailyAttendanceList = this.attendanceCache[`${pageNo}`] || []
        this.disableButton = this.dayViewCount > this.RECORD_LIMIT
      } catch (err) {
        this.isLoading = false
      } finally {
        this.isLoading = false
        this.checkIfAllPagesVisited()
      }
      return this.dailyAttendanceList
    },
    objectDeepCopy,
    utcOffsetZero,
    isEmpty,
    checkIfAllPagesVisited() {
      const totalPages = Math.ceil(this.dayViewCount / this.RECORD_LIMIT)
      const visitedPages = Object.keys(this.attendanceCache).length

      if (visitedPages >= totalPages) {
        this.disableButton = false
      } else {
        this.disableButton = true
      }
    },
    allCheckForFilterRecord() {
      if (Array.isArray(this.currentList)) {
        let allPresent = !this.currentList.some((student) => student.status !== true)

        let allTardy = !this.currentList.some((student) => student.isTardy !== true)

        this.AllChecked = allPresent
        this.AllTardyChecked = allTardy
      }
    },
    setAllChecked(studentStatus) {
      this.currentList.map((student) => {
        student.status = studentStatus
        if (studentStatus) student.isTardy = false
      })
      this.allCheckForFilterRecord()
      this.forceUpdate = this.forceUpdate + 2
    },
    setAllTardyChecked(tardyStatus) {
      this.currentList.map((student) => {
        student.isTardy = tardyStatus
        if (tardyStatus) {
          student.status = false
        }
      })
      this.allCheckForFilterRecord()
      this.forceUpdate = this.forceUpdate + 2
    },

    updateStatus(index, event) {
      let checked = event.status
      this.currentList[index].status = event.status
      if (checked) {
        this.currentList[index].isTardy = false
      }
      this.allCheckForFilterRecord()
    },
    updateTardy(index, event) {
      let checked = event.status
      this.currentList[index].isTardy = event.status

      if (checked) this.currentList[index].status = false

      this.allCheckForFilterRecord()
      this.forceUpdate++
    },
    updateComment(index, event) {
      this.currentList[index].comment = event.target.value
    },
    async formCheck() {
      try {
        this.disableButton = true
        this.updateAllRecordsList()
        const payLoadList =
          this.dailyAttendanceList?.length > 0 ? this.dailyAttendanceList : this.studentList
        const mappedList = this.mapAttendanceStatus(payLoadList)

        this.isApiLoading = true
        const data = {
          attendances: mappedList,
        }
        await this.markDailyAttendance(data)
        this.loading = false
        this.$emit('toggle', 'fetch')
      } catch (err) {
        this.isApiLoading = false
      } finally {
        this.isLoading = false
        this.checkIfAllPagesVisited()
      }
    },

    async loadStudentList(range) {
      try {
        this.isLoading = true
        const paginationPayload = paginationRangeHandler(range)
        const pageNo = getPageNumberWithRange(paginationPayload, this.RECORD_LIMIT)
        const cachedPage = this.attendanceCache?.[`${pageNo}`]
        const isPageAlreadyExists = Array.isArray(cachedPage) && cachedPage.length > 0
        let payload = {
          ...paginationPayload,
        }
        if (isPageAlreadyExists) {
          this.studentList = this.attendanceCache[`${pageNo}`] || []
          this.isLoading = false
          return
        }
        const response = await this.getStudentsWithFilters(payload)
        if (response?.records === 0) return
        const result = objectDeepCopy(response.records || [])
        this.isLoading = false
        this.attendanceCache = {
          ...this.attendanceCache,
          [pageNo]: result,
        }
        this.dayViewCount = response?.meta?.total_records
        this.studentList = this.attendanceCache[`${pageNo}`] || []
        this.allCheckForFilterRecord()
        this.disableButton = this.dayViewCount > this.RECORD_LIMIT
      } catch (err) {
        this.isLoading = false
      }
    },
    handleClick(eventName) {
      switch (eventName) {
        case 'close':
          this.$emit('toggle')
          break
        case 'cancel':
          break
      }
    },
    ...mapActions('layout', ['setLeftbarContent']),
    ...mapActions('student', ['getStudentsWithFilters']),
    ...mapActions('attendance', ['getDailyAttendanceList', 'markDailyAttendance']),
  },
}
</script>

<style lang="scss" scoped>
.disabled-btn {
  background-color: var(--primary-grey);
  border: none;
}
</style>
